/**
 * ProductCard — individual product card in results grid
 */

import { createElement } from '../utils/dom.js';
import { EVENTS } from '../core/events.js';
import formatPrice from '../utils/formatPrice.js';

export default class ProductCard {
  /**
   * @param {Object} product - Product data from API
   * @param {string} currency - Currency code
   * @param {Object} eventBus - EventBus instance
   * @param {Object} [widgetParams] - Widget display params
   */
  constructor(product, currency, eventBus, widgetParams) {
    this._product = product;
    this._currency = currency;
    this._eventBus = eventBus;
    this._params = widgetParams || {};
  }

  /**
   * Render and return the card element
   * @returns {HTMLElement}
   */
  render() {
    const p = this._product;
    const el = createElement('a', 'grb-widget--product-card', {
      href: p.product_url || '#',
      target: '_self',
    });

    // Prevent default only if no product_url
    if (!p.product_url) {
      el.addEventListener('click', (e) => e.preventDefault());
    }

    // Image container
    const imgWrap = createElement('div', 'grb-widget--product-image-wrap');
    const img = createElement('img', 'grb-widget--product-image', {
      src: p.main_image || '',
      alt: p.title || '',
      loading: 'lazy',
    });
    img.addEventListener('error', () => {
      img.style.display = 'none';
    });
    imgWrap.appendChild(img);

    // Visual search button (magnifier icon)
    if (this._params.show_visual_search !== false) {
      const vsBtn = createElement('button', 'grb-widget--visual-search-btn', {
        type: 'button',
        'aria-label': 'Visual search',
      });
      vsBtn.innerHTML = '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>';
      vsBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this._eventBus.emit(EVENTS.CROP_START, {
          imageUrl: p.main_image,
          genderId: p.gender ? p.gender.id : null,
        });
      });
      imgWrap.appendChild(vsBtn);
    }

    el.appendChild(imgWrap);

    // Content
    const content = createElement('div', 'grb-widget--product-content');

    // Brand
    if (this._params.card_show_brand !== false && p.vendor && p.vendor.name) {
      content.appendChild(createElement('div', 'grb-widget--product-brand', {}, [p.vendor.name]));
    }

    // Title
    content.appendChild(createElement('div', 'grb-widget--product-title', {}, [p.title || '']));

    // Price row
    const priceRow = createElement('div', 'grb-widget--product-price-row');

    priceRow.appendChild(
      createElement('span', 'grb-widget--product-price', {}, [formatPrice(p.price, this._currency)])
    );

    if (this._params.card_show_old_price !== false && p.old_price && p.old_price !== p.price) {
      priceRow.appendChild(
        createElement('span', 'grb-widget--product-old-price', {}, [formatPrice(p.old_price, this._currency)])
      );
    }

    content.appendChild(priceRow);

    // Sizes
    if (this._params.card_show_sizes !== false && p.sizes && p.sizes.length) {
      const sizesText = p.sizes.map((s) => s.name).join(', ');
      content.appendChild(createElement('div', 'grb-widget--product-sizes', {}, [sizesText]));
    }

    el.appendChild(content);
    return el;
  }
}
